import { Alert } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as React from "react";

import { isAbortError } from "@/shared/errors";
import {
  useMeasure,
  type OnDimensionsChange,
  useDebouncedCallback,
} from "@/shared/hooks";
import { ErrorMonitoringService } from "@/shared/services";
import {
  LayoutItem,
  LogPanelState,
  useVizDispatch,
  actions,
} from "@/shared/state/visualization";

import { type LogPanelSettingsSidebarProps } from "../../PanelSettings/LogPanelSettings";
import { SidebarViewType, useWorkspaceSidebar } from "../../sidebarContext";
import { useWorkspaceTimer } from "../../WorkspaceCtx";
import { useSelectPanel } from "../../WorkspaceCtx/hooks";
import { DataNotFoundMessage } from "../DataNotFoundMessage";
import { NoDataMessage } from "../NoDataMessage";
import { PanelHeader } from "../PanelHeader";
import { PanelLayout } from "../PanelLayout";
import { RenderingError } from "../RenderingError";

import { SetColumnsAction } from "./columnFilters/actions";
import type { ColumnDefinition } from "./columnFilters/columnDefinition";
import { isValuelessCondition } from "./columnFilters/conditionMeta";
import { generateFilterStageColors } from "./columnFilters/filterStageColors";
import { columnFiltersReducer } from "./columnFilters/reducer";
import {
  initColumnFiltersState,
  type ColumnFilter,
} from "./columnFilters/state";
import { IndexState } from "./IndexState";
import { LogManager } from "./LogManager";
import styles from "./LogPanel.module.css";
import { SearchTool } from "./logTools/SearchTool";
import { LogEvent } from "./messaging";
import { useLogPanelContext } from "./panelContext";
import { FilterBar } from "./search/FilterBar";
import {
  buildColorMap,
  prepareFilters,
  type PreparedFilters,
} from "./search/highlightUtils";
import { useSearchIndexManager } from "./search/useSearchIndexManager";
import { DEFAULT_LOG_LINE_HEIGHT, Pagination, Row, TableHeader } from "./table";
import { useLogs } from "./useLogs";

interface LogPanelProps {
  layout: LayoutItem;
  state: LogPanelState;
}

/**
 * Chosen such that MAX_LOGS_PER_PAGE * DEFAULT_LOG_LINE_HEIGHT is less than
 * the maximum height of a scroll element (which varies browser by browser, but tops out aroun 17M pixels tall).
 */
const MAX_LOGS_PER_PAGE = 250_000;

/**
 * Project virtualized index in pagination-space to actual index in data-space.
 */
function virtualToDataIndex(virtualIndex: number, currentPage: number) {
  return currentPage * MAX_LOGS_PER_PAGE + virtualIndex;
}

const VIRTUAL_GRID_INLINE_STYLES = Object.freeze({
  "--row-height": `${DEFAULT_LOG_LINE_HEIGHT}px`,
}) as React.CSSProperties;

// Stable empty reference so memoized rows bail out when search is hidden
// and filter state hasn't changed.
const EMPTY_PREPARED_FILTERS: PreparedFilters = new Map();

export function LogPanel({ layout, state }: LogPanelProps) {
  const timer = useWorkspaceTimer();
  const sidebar = useWorkspaceSidebar();
  const selectPanel = useSelectPanel();
  const dispatch = useVizDispatch();
  const [logManager, setLogManager] = React.useState<LogManager | null>(null);
  const [loadingState, setLoadingState] =
    React.useState<ReturnType<LogManager["downloadState"]>>("foreground");

  const [error, setError] = React.useState<Error | null>(null);

  // Message::sequence is used as the unique identifier for tracking expanded state
  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());

  // Message::logTime is used as the unique identifier for tracking expanded and selected states
  const [selected, setSelected] = React.useState<bigint | undefined>();

  // The number of messages in the topic is the number of rows that an unfiltered
  // log panel will show. This is useful when the panel is still loading
  // and is displaying skeletons.
  //
  // As a heuristic, the log panel only shows data from one topic,
  // so that message count can be extracted from any data entry in the panel state.
  //
  // As a fallback, if the message count is not available on the topic for some reason,
  // assume 20 log lines to show a good number of skeletons during the data load.
  const topicMessageCount = state.data[0]?.topic?.messageCount ?? 20;

  // The number of logs indexed by timestamp only in the log search engine.
  const [indexedLogCount, setIndexedLogCount] = React.useState(0);

  const [dynamicColumns, setDynamicColumns] = React.useState<
    ColumnDefinition[]
  >([]);

  // The number of logs that the panel should display.
  //
  // If a non-zero number of logs have been loaded, the indexed log count is the
  // source of truth always. The log search engine can apply filters to the log index,
  // which changes the total number of logs that will be shown (even 0).
  //
  // However, during the initial load of the log time index, the indexed
  // log count will be zero. Respecting the indexed log count in this situation means
  // that no logs or loading state will be visible in the panel.
  //
  // As a fallback, use the topic message count to make sure a reasonable number
  // of row skeletons are shown while data is loading.
  const panelLogCount = React.useMemo(() => {
    if (indexedLogCount === 0 && loadingState) {
      return topicMessageCount;
    }
    return indexedLogCount;
  }, [indexedLogCount, topicMessageCount, loadingState]);

  const [currentPage, setCurrentPage] = React.useState(0); // 0-indexed

  const [searchIndexState, setSearchIndexState] = React.useState(
    IndexState.Empty,
  );

  const { isSearchVisible, toggleSearchVisibility } = useLogPanelContext();

  React.useEffect(
    function init() {
      if (error !== null) {
        return;
      }

      const abortController = new AbortController();
      const manager = new LogManager();

      manager.setEventListener(LogEvent.Initialized, () => {
        if (abortController.signal.aborted) {
          return;
        }

        setLogManager(manager);
      });

      manager.setEventListener(LogEvent.Error, (err) => {
        if (abortController.signal.aborted || isAbortError(err)) {
          return;
        }

        ErrorMonitoringService.captureError(err);
        setError(err);
        setLogManager(null);
      });

      manager.setEventListener(LogEvent.ColumnsChanged, ({ columns }) => {
        if (abortController.signal.aborted) {
          return;
        }

        setDynamicColumns(columns);
      });

      manager.setEventListener(LogEvent.NumLogsChanged, ({ numLogs }) => {
        if (abortController.signal.aborted) {
          return;
        }

        setIndexedLogCount(numLogs);
      });

      return function cleanUp() {
        abortController.abort();
        manager.dispose();
      };
    },
    [error],
  );

  const dataLoadingTimerHoldRef = React.useRef<symbol | null>(null);
  React.useEffect(
    function holdTimerWhileDataLoading() {
      if (logManager === null) {
        return;
      }

      const abortController = new AbortController();

      logManager.setEventListener(LogEvent.LoadingStateChange, ({ state }) => {
        if (abortController.signal.aborted) {
          return;
        }

        setLoadingState(state);

        const isLoading = state !== false;
        if (isLoading && dataLoadingTimerHoldRef.current === null) {
          dataLoadingTimerHoldRef.current = timer.hold();
        }
        if (!isLoading && dataLoadingTimerHoldRef.current !== null) {
          timer.holdRelease(dataLoadingTimerHoldRef.current);
          dataLoadingTimerHoldRef.current = null;
        }
      });

      return function cleanUp() {
        abortController.abort();
        if (dataLoadingTimerHoldRef.current !== null) {
          timer.holdRelease(dataLoadingTimerHoldRef.current);
          dataLoadingTimerHoldRef.current = null;
        }
      };
    },
    [logManager, timer],
  );

  /**
   * Hold the timer while the search index is building.
   * Building the search index is resource intensive, we don't want playback held up by it.
   */
  const searchIndexBuildingTimerHoldRef = React.useRef<symbol | null>(null);
  React.useEffect(
    function holdTimerWhileSearchIndexBuilding() {
      if (logManager === null) {
        return;
      }

      const abortController = new AbortController();

      logManager.setEventListener(
        LogEvent.SearchIndexStateChange,
        ({ state }) => {
          if (abortController.signal.aborted) {
            return;
          }

          switch (state) {
            case IndexState.Building: {
              if (searchIndexBuildingTimerHoldRef.current === null) {
                searchIndexBuildingTimerHoldRef.current = timer.hold();
              }
              break;
            }
            case IndexState.Built: // FALL THROUGH
            case IndexState.Empty: {
              if (searchIndexBuildingTimerHoldRef.current !== null) {
                timer.holdRelease(searchIndexBuildingTimerHoldRef.current);
                searchIndexBuildingTimerHoldRef.current = null;
              }
              break;
            }
          }

          setSearchIndexState(state);
        },
      );

      return function cleanUp() {
        abortController.abort();
        if (searchIndexBuildingTimerHoldRef.current !== null) {
          timer.holdRelease(searchIndexBuildingTimerHoldRef.current);
          searchIndexBuildingTimerHoldRef.current = null;
        }
      };
    },
    [logManager, timer],
  );

  React.useEffect(
    function setState() {
      if (logManager === null) {
        return;
      }

      const abortController = new AbortController();

      logManager.setState(state.data, abortController.signal);

      return function cleanUp() {
        abortController.abort();
      };
    },
    [state.data, logManager],
  );

  const { buildSearchIndex, disposeSearchIndex } =
    useSearchIndexManager(logManager);

  const A_LOT_OF_LOGS = 100_000; // 2025-03-17(GM): result of convo w/Benji

  /**
   * Whether we need confirmation before building the search index.
   * FilterBar uses this to decide between auto-showing a progress indicator
   * (small counts) vs. prompting the user with a Build/Dismiss alert (large
   * counts, where building is expensive and may cause memory issues).
   */
  const needsConfirmation =
    logManager !== null &&
    logManager.isSearchIndexEmpty &&
    topicMessageCount > A_LOT_OF_LOGS;

  // Build search index automatically if search is visible and index is empty
  // (only for small log counts — large log counts are gated on an inline
  // confirmation alert rendered by FilterBar).
  React.useEffect(
    function autoBuildSearchIndexForSmallPanels() {
      if (logManager === null || !isSearchVisible) {
        return;
      }

      if (!logManager.isSearchIndexEmpty) {
        return;
      }

      if (!needsConfirmation) {
        buildSearchIndex();
      }
    },
    [logManager, isSearchVisible, needsConfirmation, buildSearchIndex],
  );

  // Handler for when the search tool button is clicked
  const handleSearchToolClick = React.useCallback(() => {
    if (logManager === null) {
      return;
    }
    if (isSearchVisible) {
      // Dispose the search index when closing the filter bar to free memory.
      // The index will be rebuilt when the user re-opens the filter bar.
      disposeSearchIndex().catch((err: unknown) =>
        ErrorMonitoringService.captureError(err),
      );
    }
    toggleSearchVisibility();
  }, [logManager, isSearchVisible, toggleSearchVisibility, disposeSearchIndex]);

  const tableRef = React.useRef<HTMLDivElement>(null);
  const hasInitiatedTimerUpdateRef = React.useRef<boolean>(false);

  const staticColumns = ["time"];
  const numColumns = dynamicColumns.length + staticColumns.length;

  const [columnFiltersState, columnFiltersDispatch] = React.useReducer(
    columnFiltersReducer,
    { dynamicColumns, savedFilters: state.config?.columnFilters },
    (init) => initColumnFiltersState(init.dynamicColumns, init.savedFilters),
  );

  const hasReceivedColumnsRef = React.useRef(false);
  React.useEffect(
    function updateFilterStateColumns() {
      if (dynamicColumns.length > 0) {
        hasReceivedColumnsRef.current = true;
      }
      // Skip when dynamicColumns is empty on initial mount (before ColumnsChanged).
      // The reducer init already handles empty columns; dispatching
      // SetColumnsAction([]) here would prune saved per-column filters
      // before the real column set has arrived.
      //
      // However, once real columns have arrived, allow dispatching empty
      // columns so that stale filters are pruned when all data sources
      // are removed from the panel.
      if (dynamicColumns.length === 0 && !hasReceivedColumnsRef.current) {
        return;
      }
      columnFiltersDispatch(new SetColumnsAction(dynamicColumns));
    },
    [dynamicColumns],
  );

  // Compute colorMap, stage colors, and prepared filters once for all rows
  // instead of per-row.
  //
  // The ref carries the previous colorMap into the next rebuild so that
  // buildColorMap can preserve existing color indices — adding or removing
  // a filter never reshuffles colors on unrelated filters.
  const prevColorMapRef = React.useRef<Map<string, number>>(new Map());
  const colorMap = React.useMemo(
    () => buildColorMap(columnFiltersState.filters, prevColorMapRef.current),
    [columnFiltersState.filters],
  );
  React.useEffect(() => {
    prevColorMapRef.current = colorMap;
  }, [colorMap]);
  // Palette must be long enough to cover the largest assigned index, not just
  // the number of entries — indices can be gappy between a removal and the
  // next addition that fills the gap.
  const stageColors = React.useMemo(() => {
    let max = -1;
    for (const idx of colorMap.values()) {
      if (idx > max) {
        max = idx;
      }
    }
    return generateFilterStageColors(max + 1);
  }, [colorMap]);
  // When search is hidden, hand rows an empty PreparedFilters so highlight
  // spans stop rendering. Filter state itself is preserved for when search
  // re-opens.
  const preparedFilters = React.useMemo(
    () =>
      isSearchVisible
        ? prepareFilters(columnFiltersState, colorMap)
        : EMPTY_PREPARED_FILTERS,
    [isSearchVisible, columnFiltersState, colorMap],
  );

  const getItemKey = React.useCallback(
    function getItemKey(index: number) {
      const dataIndex = virtualToDataIndex(index, currentPage);
      return logManager?.getLogByIndex(dataIndex)?.sequence ?? dataIndex;
    },
    [logManager, currentPage],
  );

  const logCountForCurrentPage = React.useMemo(() => {
    const startIndex = currentPage * MAX_LOGS_PER_PAGE;
    const remainingLogs = Math.max(0, panelLogCount - startIndex);
    return Math.min(remainingLogs, MAX_LOGS_PER_PAGE);
  }, [currentPage, panelLogCount]);

  const virtualizer = useVirtualizer({
    count: logCountForCurrentPage,
    // Value chosen to match the loading row height
    estimateSize: () => DEFAULT_LOG_LINE_HEIGHT,
    getItemKey,
    getScrollElement: () => tableRef.current,
    // Value chosen through trial and error
    overscan: 5,
    paddingStart: DEFAULT_LOG_LINE_HEIGHT, // account for header
    useAnimationFrameWithResizeObserver: true,
  });
  const items = virtualizer.getVirtualItems();

  useLogs(logManager, virtualizer, timer, currentPage, MAX_LOGS_PER_PAGE, {
    enabled: state.data.length > 0,
  });

  const scrollTable = React.useCallback(
    function scrollTable() {
      if (logManager === null) {
        return;
      }

      // Don't scroll when this panel has initiated a timer update
      if (hasInitiatedTimerUpdateRef.current) {
        hasInitiatedTimerUpdateRef.current = false;
        return;
      }

      // Closest index as of (equal to or before) currentTime
      const dataIndexAsOfTime = logManager.findLogIndexForTime(
        timer.currentTime,
      );
      const logTimeAsOfTime = logManager.getTimestampByIndex(dataIndexAsOfTime);

      // Calculate which page this index falls within
      const targetPage = Math.floor(dataIndexAsOfTime / MAX_LOGS_PER_PAGE);
      if (targetPage !== currentPage) {
        setCurrentPage(targetPage);
      }

      // Map to virtual index
      const indexWithinPage = dataIndexAsOfTime % MAX_LOGS_PER_PAGE;

      // 2025-04-02 (GM)
      // While the virtualizer exposes a "scrollToIndex" method,
      // under certain conditions that happen to match ours,
      // it will continuously, recursively call itself in an attempt to adjust for dynamic sizing.
      // https://github.com/TanStack/virtual/blob/main/packages/virtual-core/src/index.ts#L997-L1016
      //
      // Combing through relevant GH issues didn't provide a better resolution:
      //  - https://github.com/TanStack/virtual/issues/925
      //  - https://github.com/TanStack/virtual/issues/467
      const offsetAndAlign = virtualizer.getOffsetForIndex(
        indexWithinPage,
        "center",
      );
      if (offsetAndAlign === undefined) {
        return;
      }
      const [offset] = offsetAndAlign;
      virtualizer.scrollToOffset(offset);

      if (logTimeAsOfTime !== undefined) {
        setSelected(logTimeAsOfTime);
      }
    },
    [logManager, timer, virtualizer, currentPage],
  );

  /**
   * When log indices have changed, reset scroll state.
   */
  React.useEffect(scrollTable, [indexedLogCount, scrollTable]);

  React.useEffect(
    function setTimerCallbacks() {
      const abortController = new AbortController();

      timer.addListener("tick", scrollTable, {
        signal: abortController.signal,
      });
      timer.addListener("seek", scrollTable, {
        signal: abortController.signal,
      });

      return function abort() {
        abortController.abort();
      };
    },
    [timer, scrollTable],
  );

  const onRenderingSurfaceResize = React.useCallback<OnDimensionsChange>(() => {
    if (logManager && state.data.length > 0) {
      scrollTable();
    }
  }, [logManager, state.data.length, scrollTable]);

  const onRenderingSurfaceResizeDebounced =
    useDebouncedCallback<OnDimensionsChange>(onRenderingSurfaceResize, 150);

  const [measured] = useMeasure<HTMLDivElement>({
    onDimensionsChange: onRenderingSurfaceResizeDebounced,
  });

  const hasActiveColumnFilters = Object.values(columnFiltersState.filters).some(
    (filter) =>
      filter.stages.some(
        (stage) =>
          isValuelessCondition(stage.condition) || stage.value.trim() !== "",
      ),
  );

  const onClearError = React.useCallback(() => setError(null), []);

  const onRowClick = React.useCallback(
    (logTime: bigint, sequence: number) => {
      hasInitiatedTimerUpdateRef.current = true;
      setSelected(logTime);
      timer.seekTo(logTime);
      setExpanded((prev) => {
        if (prev.has(sequence)) {
          const next = new Set(prev);
          next.delete(sequence);
          return next;
        }
        return new Set(prev).add(sequence);
      });
    },
    [timer],
  );

  const handlePageChange = React.useCallback(
    (page: number) => {
      if (logManager === null) {
        return;
      }
      setCurrentPage(page);

      // Get the first log of the new page
      const firstLogIndex = page * MAX_LOGS_PER_PAGE;
      const timestamp = logManager.getTimestampByIndex(firstLogIndex);

      if (timestamp !== undefined) {
        // Set flag to prevent scrollTable from handling this timer update
        hasInitiatedTimerUpdateRef.current = true;
        timer.seekTo(timestamp);
      }
    },
    [logManager, timer],
  );

  const totalVirtualHeight = virtualizer.getTotalSize();
  const scrollListInlineStyles = React.useMemo(() => {
    return {
      // 12.5rem is the width of the time column
      // 1fr is the width of the rest of the columns
      "--grid-template-columns": `12.5rem repeat(${numColumns - 1}, 1fr)`,
      height: `${totalVirtualHeight}px`,
    } as React.CSSProperties;
  }, [numColumns, totalVirtualHeight]);

  const onSearch = React.useCallback(
    (columnFilters: Record<string, ColumnFilter>, signal?: AbortSignal) => {
      if (logManager === null) {
        return;
      }
      logManager.filterLogs({ columnFilters }, signal).catch((err) => {
        if (isAbortError(err)) {
          return;
        }
        ErrorMonitoringService.captureError(err);
      });

      // Persist column filters to config.
      // Explicitly clear the legacy searchFilter key so it doesn't persist
      // as stale zombie state in saved configs.
      dispatch(
        actions.setLogPanelConfig(state.id, {
          columnFilters,
          searchFilter: undefined,
        }),
      );
    },
    [logManager, dispatch, state.id],
  );

  return (
    <PanelLayout
      state={state}
      layout={layout}
      header={
        <PanelHeader
          additionalTools={
            <SearchTool
              isActive={isSearchVisible || hasActiveColumnFilters}
              onClick={handleSearchToolClick}
            />
          }
          backgroundLoading={loadingState === "background"}
          className={styles.panelHeader}
          state={state}
        />
      }
    >
      <div className={styles.container} ref={measured}>
        <FilterBar
          hidden={!isSearchVisible}
          columnFilters={columnFiltersState}
          columnFiltersDispatch={columnFiltersDispatch}
          colorMap={colorMap}
          stageColors={stageColors}
          onSearch={onSearch}
          searchIndexState={searchIndexState}
          needsBuildConfirmation={needsConfirmation}
          topicMessageCount={topicMessageCount}
          onBuildIndex={buildSearchIndex}
          onDismissBuildPrompt={toggleSearchVisibility}
        />
        <div
          className={styles.virtualizedGrid}
          ref={tableRef}
          style={VIRTUAL_GRID_INLINE_STYLES}
        >
          {state.data.length > 0 &&
            hasActiveColumnFilters &&
            panelLogCount === 0 && (
              <div className={styles.noMatches}>
                <Alert severity="info">No logs match the active filters</Alert>
              </div>
            )}
          <div className={styles.scrollList} style={scrollListInlineStyles}>
            <TableHeader
              columnDefinitions={dynamicColumns}
              canFilter={isSearchVisible}
              columnFilters={columnFiltersState}
              columnFiltersDispatch={columnFiltersDispatch}
              colorMap={colorMap}
              stageColors={stageColors}
              panelId={state.id}
            />
            <div
              className={styles.window}
              style={{
                transform: `translateY(${items[0]?.start ?? 0}px)`,
              }}
            >
              {state.data.length > 0 &&
                items.map((virtualRow) => {
                  const dataIndex = virtualToDataIndex(
                    virtualRow.index,
                    currentPage,
                  );
                  const log = logManager?.getLogByIndex(dataIndex);
                  const columns = dynamicColumns.map((column) => ({
                    ...column,
                    isLoading:
                      // We're loading if the column doesn't exist in log and we're
                      // displaying the previous query while the new one is fetching.
                      column.messagePathId in (log?.data || {}) === false,
                  }));
                  const logTime = log?.time;

                  return (
                    <Row
                      columns={columns}
                      preparedFilters={preparedFilters}
                      expanded={
                        log?.sequence !== undefined &&
                        expanded.has(log.sequence)
                      }
                      key={virtualRow.key}
                      log={log}
                      onClick={onRowClick}
                      ref={virtualizer.measureElement}
                      selected={logTime !== undefined && logTime === selected}
                      stageColors={stageColors}
                      virtualIndex={virtualRow.index}
                    />
                  );
                })}
            </div>
          </div>
        </div>
        <Pagination
          currentPage={currentPage}
          disabled={loadingState !== false}
          onPageChange={handlePageChange}
          pageCount={Math.ceil(panelLogCount / MAX_LOGS_PER_PAGE)}
        />
        <NoDataMessage
          panelData={state.data}
          onAddDataAction={() => {
            // Open the sidebar and spotlight the columns field
            selectPanel(state.id);
            sidebar.openView<LogPanelSettingsSidebarProps>({
              type: SidebarViewType.PanelSettings,
              data: {
                field: "Columns",
                spotlightKey: performance.now(),
              },
            });
          }}
        />
        <DataNotFoundMessage panelId={state.id} />
        <RenderingError error={error} onClearError={onClearError} />
      </div>
    </PanelLayout>
  );
}
